// src/pages/LandingPage.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

const FEATURES = [
  {
    title: "Inspection templates",
    body: "Build your own questionnaires from scratch or work from a library of standard templates. Structure sections, questions, and compliance rules exactly the way you need them.",
    icon: "M4 6h16M4 10h16M4 14h16M4 18h16",
  },
  {
    title: "Execute inspections in the field",
    body: "Carry out inspections directly in the app. Answer questions, flag findings, add photos and notes — on any device, in any browser.",
    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  },
  {
    title: "Open point tracking",
    body: "Inspected parties can upload evidence directly on the platform. Close open points easy, evaluate uploaded evidence, accept or decline and generate when necessary intermediate reports.",
    icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0",
  },
  {
    title: "PDF reports — interim and final",
    body: "Generate professional inspection reports with one click. Interim reports for ongoing findings, final reports for completed inspections. Ready to send to clients.",
    icon: "M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z",
  },
  {
    title: "Vessel database",
    body: "Search 275,000+ vessels by name, MMSI or IMO. If available, ownership and management data is added autmoatically.",
    icon: "M3 17l3-10h12l3 10M3 17h18M7 17v2a1 1 0 001 1h8a1 1 0 001-1v-2",
  },
  {
    title: "Your inspection history",
    body: "Every inspection you complete stays in your account. Search past reports, track findings over time, and build a professional record of your work.",
    icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
  },
];

const FOR_WHO = [
  { label: "Independent inspectors", desc: "Solo professionals who need a complete inspection tool without the overhead of enterprise software." },
  { label: "Inspection companies", desc: "Teams that need a shared platform for templates, inspections, and reports — with multiple user seats." },
  { label: "Offshore projects", desc: "Project teams managing vendor audits, equipment inspections, and compliance documentation." },
];

function Icon({ path, size = 20, color = "currentColor" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
      strokeWidth={1.5} stroke={color}
      style={{ width: size, height: size, flexShrink: 0 }}>
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}

export default function LandingPage() {
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 640;
  const isTablet = windowWidth < 900;
  const navigate = useNavigate();
  const { token } = useAuth();

  // If already logged in, go straight to dashboard
  useEffect(() => {
    if (token) navigate("/dashboard", { replace: true });
  }, [token, navigate]);

  return (
    <div style={{ minHeight: "100vh", background: "#e2eaf4", fontFamily: "system-ui, -apple-system, sans-serif" }}>

      {/* ── Hero / nav ───────────────────────────────────────────────── */}
      <div style={{ background: "#1e3a5f" }}>
        {/* Nav */}
        <div style={{
          maxWidth: 1100, margin: "0 auto",
          padding: isMobile ? "16px 20px" : "20px 40px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <img
            src="/oskar_india_tango_logo.png"
            alt="Oskar India Tango"
            style={{ height: isMobile ? 34 : 48, width: "auto" }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 12 : 20 }}>
            <a href="/plans" style={{ fontSize: 13, color: "rgba(255,255,255,0.65)", textDecoration: "none" }}>
              Pricing
            </a>
            <button
              onClick={() => navigate("/login")}
              style={{
                padding: isMobile ? "7px 16px" : "8px 20px",
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.35)",
                borderRadius: 7, color: "white",
                fontSize: 13, fontWeight: 500, cursor: "pointer",
              }}
            >
              Sign in
            </button>
          </div>
        </div>

        {/* Hero */}
        <div style={{
          maxWidth: 1100, margin: "0 auto",
          padding: isMobile ? "48px 20px 56px" : "80px 40px 96px",
          textAlign: isMobile ? "center" : "left",
        }}>
          <div style={{
            display: "inline-block",
            fontSize: 11, fontWeight: 600, letterSpacing: "0.1em",
            textTransform: "uppercase", color: "#63b3ed",
            marginBottom: 20,
          }}>
            Maritime · Offshore · Industrial
          </div>
          <h1 style={{
            fontSize: isMobile ? 30 : 48,
            fontWeight: 700, color: "white",
            margin: "0 0 20px 0", lineHeight: 1.15,
            maxWidth: 680,
          }}>
            The inspection tool built for professionals.
          </h1>
          <p style={{
            fontSize: isMobile ? 15 : 17,
            color: "rgba(255,255,255,0.6)",
            margin: "0 0 36px 0",
            maxWidth: 540,
            lineHeight: 1.65,
            marginLeft: isMobile ? "auto" : 0,
            marginRight: isMobile ? "auto" : 0,
          }}>
            Create templates, execute inspections, track open points, and generate PDF reports — for independent inspectors, inspection companies, and offshore projects.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: isMobile ? "center" : "flex-start" }}>
            <button
              onClick={() => navigate("/register")}
              style={{
                padding: "12px 28px",
                background: "#63b3ed", color: "#0c2a4a",
                border: "none", borderRadius: 8,
                fontSize: 15, fontWeight: 600, cursor: "pointer",
              }}
            >
              Start free trial
            </button>
            <a href="/plans" style={{
              padding: "12px 28px",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: 8, color: "white",
              fontSize: 15, fontWeight: 500,
              textDecoration: "none",
              display: "inline-block",
            }}>
              See pricing
            </a>
          </div>
          <div style={{ marginTop: 20, fontSize: 12, color: "rgba(255,255,255,0.35)" }}>
            14-day free trial · No credit card required
          </div>
        </div>
      </div>

      {/* ── For who ──────────────────────────────────────────────────── */}
      <div style={{ background: "white", borderBottom: "1px solid #d0dcea" }}>
        <div style={{
          maxWidth: 1100, margin: "0 auto",
          padding: isMobile ? "40px 20px" : "48px 40px",
          display: "flex",
          flexDirection: isTablet ? "column" : "row",
          gap: isTablet ? 28 : 0,
        }}>
          {FOR_WHO.map((item, i) => (
            <div key={i} style={{
              flex: 1,
              paddingRight: (!isTablet && i < FOR_WHO.length - 1) ? 40 : 0,
              paddingLeft: (!isTablet && i > 0) ? 40 : 0,
              borderRight: (!isTablet && i < FOR_WHO.length - 1) ? "1px solid #e2eaf4" : "none",
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1e3a5f", marginBottom: 6 }}>
                {item.label}
              </div>
              <div style={{ fontSize: 13, color: "#6b8cae", lineHeight: 1.65 }}>
                {item.desc}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Features ─────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: isMobile ? "48px 20px" : "72px 40px" }}>
        <h2 style={{
          fontSize: isMobile ? 22 : 28, fontWeight: 700,
          color: "#1e3a5f", margin: "0 0 8px 0",
          textAlign: isMobile ? "center" : "left",
        }}>
          Everything you need for a professional inspection
        </h2>
        <p style={{
          fontSize: 14, color: "#6b8cae", margin: "0 0 48px 0",
          textAlign: isMobile ? "center" : "left",
        }}>
          From first question to final report — one tool, one place.
        </p>

        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "1fr 1fr 1fr",
          gap: 20,
        }}>
          {FEATURES.map((f, i) => (
            <div key={i} style={{
              background: "white", borderRadius: 12,
              padding: "24px 24px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              border: "1px solid #e2eaf4",
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: 9,
                background: "#e8f0fa",
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 14,
              }}>
                <Icon path={f.icon} size={18} color="#1e3a5f" />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1e3a5f", marginBottom: 6 }}>
                {f.title}
              </div>
              <div style={{ fontSize: 13, color: "#6b8cae", lineHeight: 1.65 }}>
                {f.body}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA strip ────────────────────────────────────────────────── */}
      <div style={{ background: "#1e3a5f" }}>
        <div style={{
          maxWidth: 1100, margin: "0 auto",
          padding: isMobile ? "48px 20px" : "64px 40px",
          textAlign: "center",
        }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, color: "white", margin: "0 0 12px 0" }}>
            Ready to run your first inspection?
          </h2>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", margin: "0 0 32px 0" }}>
            Solo plan at €56/month. 14-day free trial, no payment required to start.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => navigate("/register")}
              style={{
                padding: "12px 28px",
                background: "#63b3ed", color: "#0c2a4a",
                border: "none", borderRadius: 8,
                fontSize: 15, fontWeight: 600, cursor: "pointer",
              }}
            >
              Start free trial
            </button>
            <a href="/plans" style={{
              padding: "12px 28px",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: 8, color: "white",
              fontSize: 15, fontWeight: 500,
              textDecoration: "none", display: "inline-block",
            }}>
              Compare plans
            </a>
          </div>
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <div style={{ background: "#162d4a", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{
          maxWidth: 1100, margin: "0 auto",
          padding: isMobile ? "28px 20px" : "32px 40px",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "flex-start" : "center",
          justifyContent: "space-between",
          gap: 16,
        }}>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>
            © 2026 Kloos International BV · KvK 99475669 · Groningen, Netherlands
          </div>
          <div style={{ display: "flex", gap: 20 }}>
            <a href="/plans" style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", textDecoration: "none" }}>Pricing</a>
            <a href="/terms" style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", textDecoration: "none" }}>Terms</a>
            <a href="/privacy" style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", textDecoration: "none" }}>Privacy</a>
            <a href="mailto:info@oskar-india-tango.app" style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", textDecoration: "none" }}>Contact</a>
          </div>
        </div>
      </div>

    </div>
  );
}